import { useResponsive } from '@/hooks';
import { ResponsiveCollection } from '@/interfaces';
import Script from 'next/script';
import type { FC } from 'react';
import { SocialIframe, TiktokBlockQuote } from '../../MerchantSection.styles';
import { SocialIframeSource, iframeSizeDesktop, iframeSizeMobile } from '../../constants';

interface ISocialProfilePreviewProps {
  src: string;
}

const removeSlash = (url: string) => {
  if (url.endsWith('/')) {
    return url.slice(0, -1);
  }
  return url;
};

export const TiktokProfilePreview: FC<ISocialProfilePreviewProps> = ({ src }) => {
  const tiktokUsername = src.replace('https://www.tiktok.com/@', '');
  return (
    <div>
      <TiktokBlockQuote
        className='tiktok-embed'
        cite={removeSlash(src)}
        data-unique-id={tiktokUsername}
        data-embed-type='creator'
        style={{ maxWidth: 780, minWidth: 288 }}
      >
        <section>
          <a target='_blank' rel='noreferrer' href={`${src}?refer=creator_embed`}>
            {`@${tiktokUsername}`}
          </a>
        </section>
      </TiktokBlockQuote>
      <Script async src='https://www.tiktok.com/embed.js' />
    </div>
  );
};

export const InstagramProfilePreview: FC<ISocialProfilePreviewProps> = ({ src }) => {
  const { isDesktopPMT } = useResponsive([ResponsiveCollection.DesktopPMT]);

  const iframeSize = isDesktopPMT
    ? iframeSizeDesktop[SocialIframeSource.Instagram]
    : iframeSizeMobile[SocialIframeSource.Instagram];

  return (
    <SocialIframe
      title='Instagram Embed'
      className='instagram-media instagram-media-rendered'
      id='instagram-embed'
      src={`${removeSlash(src)}/embed/`}
      allowFullScreen
      data-instgrm-payload-id='instagram-media-payload-1'
      scrolling='no'
      style={{
        background: 'white',
        maxWidth: 540,
        display: 'block',
        padding: 0,
        width: iframeSize.width,
        height: iframeSize.height,
      }}
    />
  );
};

export const FacebookProfilePreview: FC<ISocialProfilePreviewProps> = ({ src }) => {
  const { isDesktopPMT } = useResponsive([ResponsiveCollection.DesktopPMT]);

  const iframeSize = isDesktopPMT
    ? iframeSizeDesktop[SocialIframeSource.Facebook]
    : iframeSizeMobile[SocialIframeSource.Facebook];

  return (
    <SocialIframe
      title='Facebook Embed'
      src={`https://www.facebook.com/plugins/page.php?href=${removeSlash(src)}&tabs=timeline&width=${
        iframeSize.width
      }&height=${
        iframeSize.height
      }&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=true&hide_cta=true`}
      allowFullScreen
      hide-cta='true'
      width={iframeSize.width}
      height={iframeSize.height}
      allow='autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share'
    />
  );
};
